import type { ParsedUrlQuery } from 'querystring';

import { adaptPageURLToProductsQueryActiveState } from 'src/adapters/pageURLToStore/adaptPageURLToProductsQueryActiveState';
import { adaptPageURLToStore } from 'src/adapters/pageURLToStore/adaptPageURLToStore';
import { parseUrlFacets } from 'src/adapters/pageURLToStore/parseUrlFacets';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { PageType } from 'src/types/navigation';
import type { SyncProductsQueryFromURLPayload } from '../types/ProductsQueryState';

const getQueryFromAsPath = (asPath: string): ParsedUrlQuery => {
  const queryStringIndex = asPath.indexOf('?');
  if (queryStringIndex === -1) {
    return {};
  }

  const queryString = asPath.slice(queryStringIndex + 1).split('#')[0];
  const params = new URLSearchParams(queryString);
  const query: ParsedUrlQuery = {};

  params.forEach((value, key) => {
    const existingValue = query[key];

    if (existingValue === undefined) {
      query[key] = value;
      return;
    }

    if (Array.isArray(existingValue)) {
      existingValue.push(value);
      return;
    }

    query[key] = [existingValue, value];
  });

  return query;
};

/**
 * Builds the `syncFromURL` payload from a full URL + current Redux state.
 */
export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  state: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload;

export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  query: ParsedUrlQuery,
  state: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload;

export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  queryOrState: ParsedUrlQuery | Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
  maybeState?: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload {
  const query = maybeState ? (queryOrState as ParsedUrlQuery) : getQueryFromAsPath(asPath);
  const state = (maybeState ?? queryOrState) as Pick<
    ReduxApplicationState,
    'navigation' | 'productsQuery'
  >;

  const urlFacets = parseUrlFacets(asPath, state.productsQuery.taxonomyAttributeAliases);

  const activeFacetValuesByKey: Record<string, string[]> = {};
  urlFacets.forEach(({ alias, values }) => {
    activeFacetValuesByKey[alias] = values;
  });

  const processedQuery = adaptPageURLToStore(query);

  return adaptPageURLToProductsQueryActiveState(processedQuery, {
    filterPathValues: activeFacetValuesByKey,
    includeSearchTerm: state.navigation.pageType === PageType.Search,
  });
}
